import { THE_DISTANT_FUTURE } from 'utils/constants';
import {
  Tenant,
  InviteStatus,
  PermitStatus,
  Permit,
  PermitType,
  UnitOfTime,
  Status,
  PhoneType,
  Rule,
  Ruleset,
  PropertyType,
  Property,
  User,
  PropertyGroup,
  EntityTypes,
  FileType,
  FileMetadata,
  Location,
  System,
  TenantGroup,
  DoNotTow,
  DisplayMode,
  JobType,
  PropertyInstruction,
} from 'types';
import { DEFAULT_BRANDING } from 'context/themeContext';

// These functions are essentially constructors for entities
// they return a typesafe object with default/empty values and the correct tenant ID
// they are used to initialize the form state of the entity
// but that doesn't mean the API will always accept them as is

export const getBlankPermit = (tenantID: number): Permit => ({
  id: 0,
  inviteStatus: InviteStatus.None,
  permitStatus: PermitStatus.Active,
  permitNumber: '',
  startTime: Date.now(),
  endTime: THE_DISTANT_FUTURE,
  vehicle: {
    id: 0,
    make: '',
    model: '',
    year: 0,
    color: '',
    plate: {
      state: '',
      number: '',
    },
    vin: '',
  },
  property: getBlankProperty(tenantID),
  contact: getBlankContact(tenantID),
  permitType: getBlankPermitType(tenantID),
  tenant: { id: tenantID },
  data: {},
  transactions: [],
});
export const getBlankPermitType = (tenantID: number): PermitType => ({
  id: 0,
  name: '', // Residential, Commercial, etc
  description: '',
  status: Status.Active,
  tenantID: tenantID,
  permitPricingID: 0,
  startDate: new Date(),
  purchaseLeadTime: 0, // hours
  data: {
    selfService: false,
    needsApproval: false,
    notifications: [],
    defaultTerm: {
      unitOfTime: UnitOfTime.Forever,
      amountOfTime: 1,
    },
    quantityOfSpots: 0,
    fieldConfig: {
      vin: {
        enabled: false,
        required: false,
      },
      color: {
        enabled: true,
        required: false,
      },
      makeModel: {
        enabled: true,
        required: false,
      },
      driverLicense: {
        enabled: false,
        required: false,
      },
    },
  },
});
export const getBlankContact = (tenantID: number) => ({
  id: 0,
  name: '',
  description: '',
  status: Status.Active,
  primaryEmail: '',
  primaryPhone: '',
  primaryPhoneType: PhoneType.None,
  secondaryEmail: '',
  secondaryPhone: '',
  secondaryPhoneType: PhoneType.None,
  website: '',
  tertiaryPhone: '',
  tertiaryPhoneType: PhoneType.None,
  tenantID,
  locationID: 0,
  location: {
    id: 0,
    street: '',
    unit: '',
    city: '',
    stateCode: '',
    postalCode: '',
    country: '',
    latitude: 0,
    longitude: 0,
  },
  data: {
    driverLicense: {
      number: '',
      state: '',
    },
  },
});
export const getBlankRule = (tenantID: number): Rule => ({
  id: 0,
  name: '',
  description: '',
  status: Status.Active,
  tenantID,
  startDate: new Date(),
  endDate: null,
  data: {
    ruleType: JobType.TOW,
    violations: [],
    holidayExceptions: [],
    enforcementSchedule: {
      sunday: [{ startTime: '0000', endTime: '2359' }],
      monday: [{ startTime: '0000', endTime: '2359' }],
      tuesday: [{ startTime: '0000', endTime: '2359' }],
      wednesday: [{ startTime: '0000', endTime: '2359' }],
      thursday: [{ startTime: '0000', endTime: '2359' }],
      friday: [{ startTime: '0000', endTime: '2359' }],
      saturday: [{ startTime: '0000', endTime: '2359' }],
    },
    tagHours: 0,
  },
});
export const getBlankRuleset = (tenantID: number): Ruleset => ({
  id: 0,
  tenantID: tenantID,
  name: '',
  description: '',
  status: Status.Active,
  rules: [],
});
export const getBlankProperty = (tenantID: number): Property => ({
  id: 0,
  name: '',
  description: '',
  type: PropertyType.Residential,
  externalID: '',
  status: Status.Active,
  tenantID: tenantID,
  contacts: [],
  users: [],
  rules: [],
  rulesets: [],
  permitTypes: [],
  qrCodes: [],
  location: {
    street: '',
    city: '',
    stateCode: '',
    postalCode: '',
    country: 'US',
  },
  data: {
    filterLabels: [],
    timeZone: 'America/New_York',
    settings: {
      gateCode: '',
      notes: '',
      requirePMApproval: [],
      notifyPM: [],
      enablePatrols: false,
      notifyPMPendingPermit: false,
    },
    branding: DEFAULT_BRANDING,
    disableTowing: false,
    disableFindMyCar: false,
  },
  assignDefaultRule: false,
});
export const getBlankUser = (tenantID: number): User => ({
  id: 0,
  name: '',
  firstName: '',
  lastName: '',
  status: Status.Pending,
  email: '',
  tenantIDs: [tenantID],
  lastAccessTime: new Date(),
  userData: {
    notifications: {
      allowNotifications: true,
      notificationType: {
        email: false,
        text: false,
        push: false,
      },
    },
  },
  roles: [],
  externalID: '',
  contact: {
    id: 0,
    name: '',
    description: '',
    primaryEmail: '',
    secondaryEmail: '',
    primaryPhone: '',
    primaryPhoneType: PhoneType.None,
    secondaryPhone: '',
    secondaryPhoneType: PhoneType.None,
    tertiaryPhone: '',
    tertiaryPhoneType: PhoneType.None,
    tenantID: tenantID,
    status: Status.Active,
    location: {
      id: 0,
      street: '',
      unit: '',
      city: '',
      stateCode: '',
      postalCode: '',
      country: '',
    },
  },
});
export const getBlankPropertyGroup = (tenantID: number): PropertyGroup => {
  return {
    id: 0,
    name: '',
    description: '',
    externalID: '',
    status: Status.Active,
    tenantID,
    contactID: 0,
    properties: [],
    contacts: [],
    users: [],
    rules: [],
    rulesets: [],
    permitTypes: [],
    qrCodes: [],
    data: {
      branding: DEFAULT_BRANDING,
      settings: {
        requirePMApproval: [],
        notifyPM: [],
      },
    },
  };
};
export const getBlankQRCode = (
  tenantID: number,
  entityID: number,
): FileMetadata => {
  return {
    fileKey: '',
    createdByUserID: 0,
    tenantID: tenantID,
    entityType: EntityTypes.Property,
    entityID: entityID,
    fileType: FileType.Image,
    data: {
      title: '',
      description: '',
      permanent: true,
    },
  };
};
export const getBlankLocation = (): Location => ({
  id: 0,
  street: '',
  city: '',
  stateCode: '',
  postalCode: '',
  country: 'US',
  landmark: '',
  unit: '',
  latLng: {
    lat: 0,
    lng: 0,
  },
});
export const getBlankSystem = (): System => ({
  id: 0,
  name: '',
  webhookUrl: '',
  apiKey: '',
  tenants: [],
});
export const getBlankTenantGroup = (): TenantGroup => ({
  id: 0,
  name: '',
  description: '',
  status: Status.Active,
  externalID: '',
  accountID: '',
  data: {
    systems: [],
    maxUsers: 0,
    maxProperties: 0,
    permitsEnabled: false,
  },
  tenants: [],
  users: [],
  traxeroID: '',
});
export const getBlankTenant = (): Tenant => ({
  id: 0,
  name: '',
  status: Status.Active,
  externalID: '',
  systemName: '',
  systemID: 0,
  systemData: {
    partnerId: '',
    ppiFeatureFlags: {
      allowJobCancellation: false,
    },
  },
  accountID: '',
  traxeroID: '',
  contact: getBlankContact(0),
  publicContactInfo: getBlankContact(0),
  userSessionTimeOut: 0,
  data: {
    contactID: 0,
    timeZone: 'America/New_York',
    verifyNumberCharactersVIN: 4,
    defaultRuleID: 0,
    allowUserCreationTowerRoles: false,
    useDefaultPublicContact: false,
    enableFindMyCar: false,
    allowJobCancellationBeforeStatus: '',
    companyPath: '',
    branding: DEFAULT_BRANDING,
    filterLabels: [],
    savedImportMappings: [],
    entityMerchant: {
      merchantID: '',
      status: Status.Disabled,
      data: {},
    },
  },
});

export const getBlankDoNotTow = (
  tenantID: number,
  entityID: number,
  displayMode: DisplayMode,
): DoNotTow => ({
  id: 0,
  tenantID: tenantID,
  entityID: entityID,
  entityType:
    displayMode === 'group' ? EntityTypes.PropertyGroup : EntityTypes.Property,
  vin: '',
  reason: '',
  plate: {
    number: '',
    state: '',
  },
  status: Status.Active,
  startTime: new Date(),
});

export const getBlankInstruction = (): PropertyInstruction => ({
  id: 0,
  title: '',
  text: '',
  updated: Date.now(),
  fileKey: '',
  url: '',
});
